import { get } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { toastApiError } from "../../helpers/apiError"
import { formatDate } from "../../helpers/formatDate"
import { getUserAvatarUrl, resolveMediaUrl } from "../../helpers/userAvatar"
import { getUserFullName } from "../../helpers/userDisplay"

export const PHOTOGRAPHY_DETAIL_WITH = ["medias", "city", "uploader", "tags"]
export const PHOTOGRAPHY_LIST_WITH = ["tags", "city", "medias", "uploader"]
export const PHOTOGRAPHY_WIDGET_WITH = ["medias"]
export const RECENT_PHOTOS_WIDGET_LIMIT = 9

export const PHOTO_TITLE_MAX = 120
export const PHOTO_DESCRIPTION_MAX = 200
export const MAX_PHOTO_FILES = 25
export const MAX_PHOTO_BYTES = 5 * 1024 * 1024

export const ACCEPTED_PHOTO_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "video/mp4",
]

export const PHOTO_ACCEPT =
  "image/jpeg,image/png,image/webp,video/mp4,.jpg,.jpeg,.png,.webp,.mp4"

const IMAGE_EXTENSIONS = /\.(jpe?g|png|webp)$/i
const VIDEO_EXTENSIONS = /\.mp4$/i

export const getEmptyPhotographyForm = () => ({
  title: "",
  description: "",
  tags: [],
  previews: [],
  removedMediaIds: [],
  initialMediaIds: [],
  city_id: "",
})

const sameId = (a, b) => a != null && b != null && String(a) === String(b)

const unwrapEntity = payload => {
  if (!payload || typeof payload !== "object") return null
  if (payload.id) return payload
  if (payload.data?.id) return payload.data
  return null
}

export const isVideoMime = (mime, url = "") => {
  const type = String(mime || "").toLowerCase()
  if (type.startsWith("video/")) return true
  return VIDEO_EXTENSIONS.test(String(url || ""))
}

const getDisplayName = user => getUserFullName(user)

export const resolvePhotographyMediaSrc = item => {
  const media = Array.isArray(item?.medias) ? item.medias[0] : null
  const src =
    resolveMediaUrl(media?.media_url) ||
    resolveMediaUrl(media?.url) ||
    resolveMediaUrl(media?.path_url) ||
    resolveMediaUrl(item?.media_url) ||
    null
  const thumbnailSrc =
    resolveMediaUrl(media?.thumbnail_path_url) ||
    resolveMediaUrl(media?.thumbnail_url) ||
    resolveMediaUrl(media?.thumbnail) ||
    null
  const mimeType = media?.mime_type || ""
  const video = isVideoMime(mimeType, src)

  return {
    // Prefer a generated thumbnail for video previews when the API provides one.
    src: video && thumbnailSrc ? thumbnailSrc : src || thumbnailSrc,
    mediaSrc: src,
    thumbnailSrc,
    mimeType,
    isVideo: video,
  }
}

/** Full list-card mapping used by the Photography tab. */
export const mapApiPhotography = item => {
  const tags = Array.isArray(item?.tags) ? item.tags : []
  const firstTag = tags.find(tag => tag?.name)?.name || ""
  const likes = Number(item?.likes_count ?? item?.likes ?? 0)
  const media = resolvePhotographyMediaSrc(item)

  return {
    id: item?.id,
    title: item?.title || "Untitled photo",
    src: media.mediaSrc || media.src,
    thumbSrc: media.thumbnailSrc || media.src,
    mimeType: media.mimeType,
    isVideo: media.isVideo,
    uploaderId: item?.uploader?.id || item?.uploader_id || item?.user_id || null,
    cityId: item?.city_id || item?.city?.id || null,
    photographer: {
      name: getDisplayName(item?.uploader) || "—",
      avatar: getUserAvatarUrl(item?.uploader),
    },
    likes: Number.isFinite(likes) ? Math.max(0, likes) : 0,
    liked: Boolean(item?.has_current_user_liked ?? item?.has_reacted),
    date: formatDate(item?.created_at, {
      format: "MMM D, YYYY",
      fallback: "—",
    }),
    category: firstTag,
    tags,
    raw: item,
  }
}

/** Compact thumbnail mapping for the City Hub aside widget. */
export const mapPhotographyWidgetItem = item => {
  const media = resolvePhotographyMediaSrc(item)
  return {
    id: item?.id,
    // Widget tiles need a playable/previewable URL; keep the real media file for videos.
    src: media.mediaSrc || media.src,
    thumbSrc: media.thumbnailSrc || null,
    isVideo: media.isVideo,
    title: item?.title || "Photo",
  }
}

const unwrapPhotographyList = result => {
  if (Array.isArray(result)) return result
  if (Array.isArray(result?.data)) return result.data
  return []
}

/**
 * Top recent photography items for a city (aside widget).
 * Same endpoint / city filter as the Photography tab.
 */
export const fetchRecentCityPhotos = async ({
  cityId,
  limit = RECENT_PHOTOS_WIDGET_LIMIT,
  signal,
} = {}) => {
  if (!cityId) return { ok: false, items: [] }

  try {
    const response = await get(endpoints.getPhotography, {
      params: {
        page: 1,
        per_page: limit,
        "with[]": PHOTOGRAPHY_WIDGET_WITH,
        "city_ids[]": cityId,
      },
      signal,
      skipErrorToast: true,
    })

    if (signal?.aborted) return { ok: false, aborted: true, items: [] }

    if (!response?.data?.success) {
      return { ok: false, items: [] }
    }

    const items = unwrapPhotographyList(response.data.result)
      .map(mapPhotographyWidgetItem)
      .filter(item => item.id && item.src)
      .slice(0, limit)

    return { ok: true, items }
  } catch (err) {
    if (signal?.aborted || err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
      return { ok: false, aborted: true, items: [] }
    }
    return { ok: false, items: [] }
  }
}

export const isAcceptedPhotoFile = file => {
  if (!file) return false
  const type = String(file.type || "").toLowerCase()
  if (ACCEPTED_PHOTO_TYPES.includes(type)) return true
  const name = String(file.name || "")
  return IMAGE_EXTENSIONS.test(name) || VIDEO_EXTENSIONS.test(name)
}

export const revokePhotoPreview = item => {
  if (item?.url && String(item.url).startsWith("blob:")) {
    URL.revokeObjectURL(item.url)
  }
}

export const revokePhotoPreviews = (previews = []) => {
  previews.forEach(revokePhotoPreview)
}

const appendArray = (formData, key, values = []) => {
  values.forEach(value => {
    if (value == null || value === "") return
    formData.append(`${key}[]`, value)
  })
}

const resolveMediaId = media => {
  const id =
    media?.id ||
    media?.media_id ||
    media?.mediaId ||
    media?.uuid ||
    media?.media?.id ||
    null
  return id != null && String(id).trim() !== "" ? String(id) : null
}

export const mapExistingPhotoMedia = media => {
  const mediaId = resolveMediaId(media)
  const url =
    resolveMediaUrl(media?.media_url) ||
    resolveMediaUrl(media?.thumbnail_path_url) ||
    resolveMediaUrl(media?.path_url) ||
    resolveMediaUrl(media?.url) ||
    resolveMediaUrl(media?.thumbnail) ||
    resolveMediaUrl(media?.path) ||
    null
  return {
    id: mediaId,
    mediaId,
    url,
    name: media?.name || "Existing media",
    mimeType: media?.mime_type || "",
    isExisting: true,
    file: null,
  }
}

export const mapFileToPhotoPreview = file => ({
  id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
  mediaId: null,
  url: URL.createObjectURL(file),
  name: file.name,
  mimeType: file.type,
  isExisting: false,
  file,
})

export const mapPhotographyToForm = item => {
  const tags = Array.isArray(item?.tags)
    ? item.tags.map(tag => tag?.id || tag).filter(Boolean)
    : []
  const previews = (Array.isArray(item?.medias) ? item.medias : [])
    .map(mapExistingPhotoMedia)
    .filter(media => media.mediaId)
  const initialMediaIds = previews.map(media => media.mediaId)

  return {
    title: String(item?.title || "").slice(0, PHOTO_TITLE_MAX),
    description: String(item?.description || "").slice(0, PHOTO_DESCRIPTION_MAX),
    tags,
    previews,
    removedMediaIds: [],
    initialMediaIds,
    city_id: item?.city_id || item?.city?.id || "",
  }
}

/**
 * Build photography create/update FormData.
 * - tags[] = current tag ids
 * - medias[] = only NEW files being added
 * - remove_media_ids[] = existing media ids removed or replaced (edit only)
 * Existing images that stay are not re-uploaded.
 */
export const buildPhotographyFormData = ({
  cityId,
  values,
  isEdit = false,
} = {}) => {
  const formData = new FormData()
  // Laravel does not parse multipart bodies on real PATCH — spoof via POST.
  if (isEdit) formData.append("_method", "PATCH")

  const resolvedCityId = values?.city_id || cityId
  if (resolvedCityId) formData.append("city_id", resolvedCityId)
  formData.append("title", String(values?.title || "").trim())
  formData.append("description", String(values?.description || "").trim())
  appendArray(formData, "tags", values?.tags || [])

  const previews = Array.isArray(values?.previews) ? values.previews : []
  const newFiles = previews
    .map(item => item?.file)
    .filter(file => file instanceof File)
    .slice(0, MAX_PHOTO_FILES)

  newFiles.forEach(file => {
    formData.append("medias[]", file)
  })

  if (isEdit) {
    const keptExistingIds = new Set(
      previews
        .filter(item => item?.isExisting && item?.mediaId)
        .map(item => String(item.mediaId)),
    )
    const initialIds = (
      Array.isArray(values?.initialMediaIds) ? values.initialMediaIds : []
    )
      .map(id => String(id || "").trim())
      .filter(Boolean)
    const explicitRemoved = (
      Array.isArray(values?.removedMediaIds) ? values.removedMediaIds : []
    )
      .map(id => String(id || "").trim())
      .filter(Boolean)

    const removedIds = [
      ...explicitRemoved,
      ...initialIds.filter(id => !keptExistingIds.has(id)),
    ]

    appendArray(formData, "remove_media_ids", [...new Set(removedIds)])
  }

  return formData
}

export const canManagePhotography = (item, currentUser) => {
  if (!item || !currentUser?.id) return false
  const ownerId =
    item.uploaderId ||
    item.raw?.uploader?.id ||
    item.raw?.uploader_id ||
    item.raw?.user_id ||
    item.uploader?.id
  return Boolean(ownerId && sameId(ownerId, currentUser.id))
}

export const fetchPhotographyDetail = async (id, { signal } = {}) => {
  if (!id) return { ok: false }

  const response = await get(endpoints.getPhotographyById(id), {
    params: { "with[]": PHOTOGRAPHY_DETAIL_WITH },
    signal,
  })

  if (signal?.aborted) return { ok: false, aborted: true }
  if (!response) return { ok: false, aborted: Boolean(signal?.aborted) }

  if (!response?.data?.success) {
    toastApiError(response, "Failed to load photography")
    return { ok: false }
  }

  const item = unwrapEntity(response.data.result)
  if (!item?.id) {
    toastApiError(response, "Failed to load photography")
    return { ok: false }
  }

  return { ok: true, item }
}

/** Guest-safe photography detail for public city pages. */
export const fetchPublicPhotographyDetail = async (id, { signal } = {}) => {
  if (!id) return { ok: false }

  const response = await get(endpoints.getPublicPhotographyById(id), {
    params: { "with[]": PHOTOGRAPHY_DETAIL_WITH },
    signal,
    skipErrorToast: true,
  })

  if (signal?.aborted) return { ok: false, aborted: true }
  if (!response) return { ok: false, aborted: Boolean(signal?.aborted) }

  if (!response?.data?.success) {
    return { ok: false, response }
  }

  const item = unwrapEntity(response.data.result)
  if (!item?.id) return { ok: false, response }

  return { ok: true, item }
}

/** Normalize detail medias for the preview carousel. */
export const mapPhotographyDetailMedias = item => {
  const medias = Array.isArray(item?.medias) ? item.medias : []
  return medias
    .map(media => {
      const url =
        resolveMediaUrl(media?.media_url) ||
        resolveMediaUrl(media?.thumbnail_path_url) ||
        resolveMediaUrl(media?.path_url) ||
        resolveMediaUrl(media?.url) ||
        resolveMediaUrl(media?.thumbnail) ||
        resolveMediaUrl(media?.path) ||
        null
      return {
        id: media?.id || url,
        url,
        mimeType: media?.mime_type || "",
        isVideo: isVideoMime(media?.mime_type, url),
      }
    })
    .filter(media => media.id && media.url)
}

export const mapPhotographyDetailTags = item => {
  const tags = Array.isArray(item?.tags) ? item.tags : []
  return tags
    .map(tag => ({
      id: tag?.id,
      name: String(tag?.name || "").trim(),
    }))
    .filter(tag => tag.id && tag.name)
}

const unwrapPagedList = result => {
  if (Array.isArray(result)) return result
  if (Array.isArray(result?.data)) return result.data
  return []
}

const REACTION_PAGE_SIZE = 50
const MAX_REACTION_PAGES = 20

export const interpretPhotographyReactResult = (response, previousLiked) => {
  const result = response?.data?.result
  const message = String(response?.data?.message || "").toLowerCase()
  const likesFromApi = Number(
    result?.likes_count ?? result?.reactions_count ?? result?.total,
  )

  let liked = !previousLiked
  if (typeof result?.has_current_user_liked === "boolean") {
    liked = result.has_current_user_liked
  } else if (typeof result?.reacted === "boolean") {
    liked = result.reacted
  } else if (/unreact|unlike|unlove|removed|deleted/.test(message)) {
    liked = false
  } else if (/\bloved\b|\bliked\b|\breacted\b/.test(message)) {
    liked = true
  } else if (result && typeof result === "object" && (result.id || result.user_id)) {
    liked = true
  } else if (result == null) {
    liked = false
  }

  return {
    liked,
    likes: Number.isFinite(likesFromApi) ? Math.max(0, likesFromApi) : null,
  }
}

export const fetchPhotographyReactionStats = async ({
  photographyId,
  userId,
  signal,
} = {}) => {
  if (!photographyId) return { ok: false, total: 0, liked: false }

  let page = 1
  let lastPage = 1
  let total = 0
  let liked = false

  do {
    const response = await get(endpoints.getPhotographyReactions, {
      params: {
        city_photography_id: photographyId,
        page,
        per_page: REACTION_PAGE_SIZE,
      },
      signal,
      skipErrorToast: true,
    })

    if (signal?.aborted) return { ok: false, aborted: true, total: 0, liked: false }
    if (!response?.data?.success) {
      return { ok: page === 1 ? false : true, total, liked }
    }

    const result = response.data.result || {}
    const list = unwrapPagedList(result)
    if (page === 1) total = Math.max(0, Number(result.total || list.length || 0))

    if (
      userId &&
      list.some(item => String(item?.user_id) === String(userId))
    ) {
      liked = true
    }

    lastPage = Number(result.last_page || 1)
    page += 1
    if (liked || !userId) break
  } while (page <= lastPage && page <= MAX_REACTION_PAGES)

  return { ok: true, total, liked }
}

export const hydratePhotographyReactions = async (
  photos = [],
  { userId, signal, force = false } = {},
) => {
  if (!Array.isArray(photos) || photos.length === 0) return photos

  const stats = await Promise.all(
    photos.map(async photo => {
      if (!photo?.id) return photo

      // Prefer reaction summary already on the list item — avoids N+1
      // GET /photography/reactions/all per card.
      if (!force) {
        const raw = photo.raw
        const hasCount =
          raw?.likes_count != null ||
          raw?.reactions_count != null ||
          (typeof raw?.likes === "number" && Number.isFinite(raw.likes))
        const hasLikedFlag =
          typeof raw?.has_current_user_liked === "boolean" ||
          typeof raw?.has_reacted === "boolean"
        if (hasCount && (!userId || hasLikedFlag)) return photo
      }

      const result = await fetchPhotographyReactionStats({
        photographyId: photo.id,
        userId,
        signal,
      })
      if (!result?.ok || result.aborted) return photo
      return {
        ...photo,
        likes: result.total,
        liked: result.liked,
      }
    }),
  )

  return stats
}

export const patchPhotographyLike = (photos, id, { liked, likes } = {}) =>
  (Array.isArray(photos) ? photos : []).map(photo => {
    if (String(photo?.id) !== String(id)) return photo
    return {
      ...photo,
      ...(liked != null ? { liked: Boolean(liked) } : {}),
      ...(likes != null ? { likes: Math.max(0, Number(likes) || 0) } : {}),
    }
  })
